/**
 * Krok APP2 bod 1 — JEDEN DOTAZ MISTO DVACETI (N+1 na seznamech).
 *
 * Seznam `/klienti` kresli dvacet radku a ke kazdemu potreboval kontakty
 * a zakazku strany. Obrazovka proto poslala dvacet dotazu na kontakty
 * a dalsich dvacet na zakazky; kancelar MV (343 zakazek) na tom narazila na
 * kvotu (429) a makler videl prazdne sloupce bez duvodu.
 *
 * Tohle je ta davka: funkce, kterou obrazovka vola PO JEDNOM id, posbira
 * vsechna id ZADANA V TEMZE TICKU a vyzada je JEDNIM dotazem. Volajici kod se
 * nemeni (`listPartyContacts(id)` dal vraci kontakty jedne strany), takze
 * zadna obrazovka nemusi mit vlastni kopii „davkoveho rezimu".
 *
 * ZAMERNE SE NIC NEPAMATUJE. Davka zije jen do konce probihajiciho ticku —
 * stejny duvod, jaky ma `fetchCaseAddressIndex` (krok NOC1 bod 2): zalozeny
 * klient nebo opraveny telefon se musi objevit hned, ne az vyprsi okno cache.
 *
 * Davku zavira MIKROTASK, ne `setTimeout`. Dva duvody: dotaz odejde o jeden
 * tick driv (prohlizec neceka na timer) a davkovani nesmi zpozdit volajiciho,
 * ktery na odpoved ceka v temze ticku — na `setTimeout(0)` dojezdil `await
 * act()` v cizich testech pruvodce (`zakazka-novy-p8f6.spec.tsx`), kde se
 * nabidka existujiciho klienta objevila az o makrotask pozdeji.
 */

/** Strop jedne davky; server vic id v jednom dotazu neprijme (400). */
const MAX_PER_REQUEST = 100;

interface Waiter<V> {
  readonly resolve: (value: V) => void;
  readonly reject: (error: unknown) => void;
}

/**
 * Z funkce „hodnoty pro seznam id" udela funkci „hodnota pro jedno id",
 * ktera soubezna volani slouci do jednoho dotazu.
 *
 * @param fetchMany jeden dotaz pro cely seznam id
 * @param missing   hodnota pro id, ktere odpoved nenese (server o nem nic nevi)
 */
export function createIdBatcher<V>(
  fetchMany: (ids: readonly string[]) => Promise<ReadonlyMap<string, V>>,
  missing: () => V,
): (id: string) => Promise<V> {
  let pending = new Map<string, Waiter<V>[]>();
  let scheduled = false;

  function flush(): void {
    scheduled = false;
    const batch = pending;
    pending = new Map<string, Waiter<V>[]>();
    const ids = [...batch.keys()];
    const chunks: string[][] = [];
    for (let from = 0; from < ids.length; from += MAX_PER_REQUEST) {
      chunks.push(ids.slice(from, from + MAX_PER_REQUEST));
    }
    void Promise.all(chunks.map((chunk) => fetchMany(chunk))).then(
      (results) => {
        const found = new Map<string, V>();
        for (const result of results) for (const [id, value] of result) found.set(id, value);
        for (const [id, waiters] of batch) {
          const value = found.has(id) ? (found.get(id) as V) : missing();
          for (const waiter of waiters) waiter.resolve(value);
        }
      },
      (error: unknown) => {
        // Selhani davky je selhani kazdeho volajiciho — stejne jako kdyz
        // selhal jeho vlastni dotaz. Obrazovky uz chyby osetrene maji.
        for (const waiters of batch.values()) for (const waiter of waiters) waiter.reject(error);
      },
    );
  }

  return (id: string): Promise<V> =>
    new Promise<V>((resolve, reject) => {
      const waiters = pending.get(id) ?? [];
      waiters.push({ resolve, reject });
      pending.set(id, waiters);
      if (!scheduled) {
        scheduled = true;
        queueMicrotask(flush);
      }
    });
}
